import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  LOCALE_ID,
  OnChanges,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { ControlContainer, FormControl, ReactiveFormsModule } from '@angular/forms';
import { IonDatetime, IonInput, IonPopover } from '@ionic/angular/standalone';
import {
  effectiveOptionalRequired,
  explicitPlaceholder,
  withOptionalAriaLabel,
  withOptionalLabelSuffix,
} from '../form-optional-display';
import { formatDateTime } from '../../utils/date.utils';

type IonInputHost = HTMLElement & {
  getInputElement?: () => Promise<HTMLInputElement>;
};

@Component({
  selector: 'crowdctrl-form-input-field',
  standalone: true,
  imports: [IonInput, IonDatetime, IonPopover, ReactiveFormsModule],
  templateUrl: './form-input-field.component.html',
  styleUrls: [
    '../form-outline-field-tokens.css',
    '../form-control-hint.css',
    './form-input-field.component.css',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class FormInputFieldComponent implements OnChanges, AfterViewInit {
  private readonly appLocaleId = inject(LOCALE_ID);
  private controlDisabledByComponent = false;

  @ViewChild('ionField', { read: ElementRef }) private ionFieldRef?: ElementRef<IonInputHost>;
  @ViewChild('dtPopover') private dtPopover?: IonPopover;

  @Input() invalid = false;
  @Input() hint = '';
  @Input() controlName?: string;
  @Input() type: string = 'text';
  @Input() label?: string;
  @Input() placeholder?: string;
  /**
   * When true: appends ` (optional)` to the label (unless already present) and clears the
   * required affordance. Use a **value-hint** {@link placeholder} (same idea as required fields,
   * e.g. “Enter name”) — never use the word “Optional” as the placeholder.
   */
  @Input() optional = false;
  @Input() required = false;
  @Input() disabled = false;
  @Input() inputmode?: string;
  @Input() minlength?: number;
  @Input() maxlength?: number;
  @Input() min?: number | string;
  @Input() max?: number | string;
  @Input() step?: number | string;
  @Input() pattern?: string;
  @Input() name?: string;
  @Input() autocomplete?: string;
  @Input() autocapitalize: string = 'sentences';
  @Input() autocorrect?: 'on' | 'off' | string;
  @Input() enterkeyhint?: string;
  /** Used for aria-label when there is no floating {@link label} (e.g. label-less layouts). */
  @Input() ariaLabel?: string;

  @Output() ionInput = new EventEmitter<Event>();
  @Output() ionChange = new EventEmitter<Event>();
  @Output() ionBlur = new EventEmitter<Event>();
  @Output() ionFocus = new EventEmitter<Event>();
  @Output() ionClear = new EventEmitter<Event>();

  protected formControl: FormControl | null = null;

  /**
   * Unique id for `ion-popover` trigger (native `datetime-local` cannot follow app `LOCALE_ID`;
   * see MDN / Chrome docs — display follows OS; use `ion-datetime` + `locale` instead).
   */
  readonly dtPopoverTriggerId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? `crowdctrl-dt-${crypto.randomUUID()}`
      : `crowdctrl-dt-${Math.random().toString(36).slice(2, 11)}`;

  get effectiveLabel(): string | undefined {
    return withOptionalLabelSuffix(this.optional, this.label);
  }

  get effectivePlaceholder(): string | undefined {
    return explicitPlaceholder(this.placeholder);
  }

  get effectiveRequired(): boolean {
    return effectiveOptionalRequired(this.required, this.optional);
  }

  get effectiveAriaLabel(): string | undefined {
    return withOptionalAriaLabel(this.optional, this.ariaLabel, this.label);
  }

  /**
   * BCP 47 tag for native date inputs (`lang` on the shadow native input, not only the host).
   * Not used for `datetime-local` with a reactive control (that path uses `ion-datetime` + `locale`).
   */
  get nativeDateInputLang(): string | undefined {
    return this.usesNativeDateLangOnIonInput() ? this.nativeDateLangBcp47 : undefined;
  }

  protected get nativeDateLangBcp47(): string {
    const id = (this.appLocaleId ?? '').trim();
    return id.length > 0 ? id : 'en-AU';
  }

  constructor(private readonly controlContainer: ControlContainer) {}

  ngAfterViewInit(): void {
    this.queueNativeDateLangSync();
  }

  ngOnChanges(): void {
    this.formControl = this.resolveFormControl();
    if (!this.formControl) {
      return;
    }

    if (this.disabled) {
      if (this.formControl.enabled) {
        this.formControl.disable({ emitEvent: false });
        this.controlDisabledByComponent = true;
      }
      return;
    }

    if (this.controlDisabledByComponent && this.formControl.disabled) {
      this.formControl.enable({ emitEvent: false });
      this.controlDisabledByComponent = false;
    }
    this.queueNativeDateLangSync();
  }

  /** Ionic often does not forward host `lang` onto the shadow native input; re-apply after render/focus. */
  onIonFieldFocus(event: Event): void {
    void this.syncNativeDateLangOnInnerInput();
    this.ionFocus.emit(event);
  }

  private queueNativeDateLangSync(): void {
    queueMicrotask(() => void this.syncNativeDateLangOnInnerInput());
  }

  /** Native `ion-input` types where we still sync `lang` onto the shadow input (not `datetime-local` + FormControl). */
  private usesNativeDateLangOnIonInput(): boolean {
    return ['date', 'month', 'time', 'week'].includes(this.type);
  }

  protected isDatetimeLocalOutlineField(): boolean {
    return this.type === 'datetime-local' && !!this.formControl;
  }

  protected get datetimeLocalWireValue(): string | undefined {
    const v = this.formControl?.value;
    if (v == null || v === '') {
      return undefined;
    }
    return typeof v === 'string' ? v : String(v);
  }

  protected get datetimeLocalDisplayText(): string {
    const v = this.formControl?.value;
    if (v == null || v === '' || (typeof v === 'string' && !v.trim())) {
      return '';
    }
    const formatted = formatDateTime(v as string | Date, this.nativeDateLangBcp47);
    return formatted === 'N/A' ? '' : formatted;
  }

  protected get isFieldDisabled(): boolean {
    return this.disabled || !!this.formControl?.disabled;
  }

  protected onIonDatetimeChange(ev: CustomEvent<{ value?: string | string[] | null }>): void {
    if (!this.formControl) {
      return;
    }
    const wire = this.toDatetimeLocalWire(ev.detail?.value);
    this.formControl.setValue(wire);
    this.formControl.markAsDirty();
    this.ionChange.emit(ev as unknown as Event);
    void this.dtPopover?.dismiss();
  }

  private toDatetimeLocalWire(value: string | string[] | null | undefined): string {
    if (value == null || value === '') {
      return '';
    }
    const s = Array.isArray(value) ? (value[0] ?? '') : value;
    if (!s) {
      return '';
    }
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) {
      return s.length >= 16 ? s.slice(0, 16) : s;
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private async syncNativeDateLangOnInnerInput(): Promise<void> {
    if (!this.usesNativeDateLangOnIonInput()) {
      return;
    }
    const host = this.ionFieldRef?.nativeElement;
    if (!host) {
      return;
    }
    const lang = this.nativeDateLangBcp47;
    try {
      if (typeof host.getInputElement === 'function') {
        const inner = await host.getInputElement();
        inner?.setAttribute('lang', lang);
        return;
      }
    } catch {
      /* fall through */
    }
    const input = host.shadowRoot?.querySelector('input');
    input?.setAttribute('lang', lang);
  }

  private resolveFormControl(): FormControl | null {
    if (!this.controlName) {
      return null;
    }

    const control = this.controlContainer.control?.get(this.controlName);
    return control instanceof FormControl ? control : null;
  }
}
